/**
 * Shared react-select styles for Finlytics.
 *
 * Uses theme.css tokens so menus stay readable in dark (default :root)
 * and light (body[data-theme='light']) modes. Dark hex fallbacks apply only
 * when a CSS variable is missing; they do not override light-theme tokens.
 */

export const FINLYTICS_SELECT_MENU_Z_INDEX = 11000;

const TOKEN = {
    inputBg: 'var(--input-bg, #0F1A2B)',
    cardBg: 'var(--card-bg, #0F1A2B)',
    rowHover: 'var(--row-hover, #16233A)',
    text1: 'var(--text-1, #E6EDF7)',
    text2: 'var(--text-2, #A9B7D0)',
    muted: 'var(--muted, #6E7E98)',
    border: 'var(--border, #1C2A40)',
    brand2: 'var(--brand-2, #2A8FFF)',
    brandGlow: 'var(--brand-glow, rgba(42, 143, 255, 0.18))',
    bg3: 'var(--bg-3, #14203A)',
    danger: 'var(--danger, #FF5D6C)',
    shadow: 'var(--shadow, 0 4px 12px rgba(0, 0, 0, 0.4))',
};

const SELECTED_OPTION_TEXT = '#FFFFFF';

/**
 * @returns {import('react-select').StylesConfig}
 */
export function getFinlyticsSelectStyles() {
    return {
        control: (provided, state) => ({
            ...provided,
            minHeight: 38,
            backgroundColor: TOKEN.inputBg,
            borderColor: state.isFocused ? TOKEN.brand2 : TOKEN.border,
            borderWidth: 1,
            borderStyle: 'solid',
            boxShadow: state.isFocused ? `0 0 0 2px ${TOKEN.brandGlow}` : 'none',
            color: TOKEN.text1,
            '&:hover': {
                borderColor: state.isFocused ? TOKEN.brand2 : TOKEN.border,
            },
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: TOKEN.cardBg,
            color: TOKEN.text1,
            border: `1px solid ${TOKEN.border}`,
            boxShadow: TOKEN.shadow,
            zIndex: FINLYTICS_SELECT_MENU_Z_INDEX,
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: FINLYTICS_SELECT_MENU_Z_INDEX,
        }),
        menuList: (provided) => ({
            ...provided,
            backgroundColor: TOKEN.cardBg,
            padding: 4,
        }),
        option: (provided, state) => {
            let backgroundColor = TOKEN.cardBg;
            let color = TOKEN.text1;

            if (state.isDisabled) {
                backgroundColor = TOKEN.cardBg;
                color = TOKEN.muted;
            } else if (state.isSelected) {
                backgroundColor = TOKEN.brand2;
                color = SELECTED_OPTION_TEXT;
            } else if (state.isFocused) {
                backgroundColor = TOKEN.rowHover;
                color = TOKEN.text1;
            }

            return {
                ...provided,
                backgroundColor,
                color,
                cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                ':active': {
                    ...provided[':active'],
                    backgroundColor: state.isDisabled
                        ? TOKEN.cardBg
                        : state.isSelected
                            ? TOKEN.brand2
                            : TOKEN.rowHover,
                },
            };
        },
        singleValue: (provided) => ({
            ...provided,
            color: TOKEN.text1,
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: TOKEN.bg3,
            border: `1px solid ${TOKEN.border}`,
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: TOKEN.text1,
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: TOKEN.text2,
            ':hover': {
                backgroundColor: TOKEN.danger,
                color: SELECTED_OPTION_TEXT,
            },
        }),
        placeholder: (provided) => ({
            ...provided,
            color: TOKEN.text2,
        }),
        input: (provided) => ({
            ...provided,
            color: TOKEN.text1,
            caretColor: TOKEN.text1,
        }),
        group: (provided) => ({
            ...provided,
        }),
        groupHeading: (provided) => ({
            ...provided,
            color: TOKEN.text2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 12,
            letterSpacing: '0.01em',
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            backgroundColor: TOKEN.border,
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            color: state.isFocused ? TOKEN.text1 : TOKEN.text2,
            ':hover': {
                color: TOKEN.text1,
            },
        }),
        clearIndicator: (provided) => ({
            ...provided,
            color: TOKEN.text2,
            ':hover': {
                color: TOKEN.text1,
            },
        }),
        valueContainer: (provided) => ({
            ...provided,
            color: TOKEN.text1,
        }),
    };
}

/**
 * Portal the menu to document.body so it is not clipped by modal overflow.
 * Safe when `document` is unavailable (tests / SSR).
 *
 * @returns {{ menuPortalTarget: HTMLElement | null, menuPosition: 'fixed' }}
 */
export function getFinlyticsSelectMenuPortalProps() {
    return {
        menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
        menuPosition: 'fixed',
    };
}
